<script lang="ts" setup>
import { useDropZone } from '@vueuse/core';
import { ref } from 'vue';

import { isNonEmptyString } from '@nora-monorepo/shared';

import type { DraftDrawLayerCreationTarget } from '#nora-ui/map/composables/useDraftDrawLayerCreation';
import { useDraftDrawLayerCreation } from '#nora-ui/map/composables/useDraftDrawLayerCreation';
import { useMapStore } from '#nora-ui/map/stores/map-store';

const { importGeoJsonFiles } = useDraftDrawLayerCreation();
const mapStore = useMapStore();

/**
 * The operation the new layer should belong to: the drop zone covers the whole multi-map
 * grid, so resolve the map the GeoJSON was actually dropped on instead of silently picking
 * the first map with an active operation.
 */
function drawLayerCreationTargetForDropTarget(event: DragEvent): DraftDrawLayerCreationTarget {
  const targetMapId = (event.target as HTMLElement | null)
    ?.closest('[data-map-id]')
    ?.getAttribute('data-map-id');

  const targetMap = isNonEmptyString(targetMapId) ? mapStore.getMapById(targetMapId) : undefined;

  if (!targetMap) {
    const operationId = mapStore.getActiveOperationId();

    return {
      map: isNonEmptyString(operationId) ? mapStore.getMapByOperationId(operationId) : undefined,
      operationId,
    };
  }

  return {
    map: targetMap,
    operationId: targetMap.ref.activeOperationId ?? targetMap.ref.visibleOperationIds[0],
  };
}

function onDrop(files: File[] | null, event: DragEvent): void {
  if (files === null) {
    return;
  }

  importGeoJsonFiles(files, drawLayerCreationTargetForDropTarget(event));
}

const dropZoneRef = ref<HTMLElement>();
const { isOverDropZone } = useDropZone(dropZoneRef, {
  onDrop,
  checkValidity: items => Array.from(items).every(item => item.kind === 'file'),
});
</script>

<template>
  <div
    ref="dropZoneRef"
    class="w-full h-full"
    :class="{ 'opacity-50': isOverDropZone }"
  >
    <slot />
  </div>
</template>
